import type { DateRange } from "react-day-picker";

import moment from "moment";

import { PRESET_GROUPS, PresetKey } from "./constants";

const makeDate = (
  year: number,
  month: number,
  day: number,
  hour: number = 0,
  minutes: number = 0,
  seconds: number = 0,
) => new Date(year, month, day, hour, minutes, seconds);

const subtractTimeFromDate = (objDate: Date, nrMilliseconds: number) => {
  const numberOfMlSeconds = objDate;
  const newDateObj = new Date(numberOfMlSeconds.getTime() - nrMilliseconds);
  return newDateObj;
};

export const toIso = (date?: Date) =>
  date ? moment(date).format("YYYY-MM-DD") : "";

export const fromIso = (iso: string) => moment(iso, "YYYY-MM-DD").toDate();

export const toEndOfDay = (date: Date): Date => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

export const formatRangeLabel = (preset?: PresetKey, range?: DateRange) => {
  if (preset && preset !== PresetKey.Custom) {
    return (
      PRESET_GROUPS.flat().find((p) => p.key === preset)?.label ??
      "datePicker:selectPeriod"
    );
  }
  if (!range?.from) return "datePicker:selectPeriod";
  if (!range.to || moment(range.from).isSame(range.to, "day")) {
    return moment(range.from).format("DD MMM YYYY");
  }
  return `${moment(range.from).format("DD MMM YYYY")} – ${moment(range.to).format("DD MMM YYYY")}`;
};

export const getPresetRange = (key: PresetKey, timezone: string): DateRange => {
  const today = toDisplayDate(new Date(), timezone);
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();

  // Rolling presets cover full days only, so they end at the end of yesterday
  // in the given timezone.
  const endOfYesterday = subtractTimeFromDate(makeDate(y, m, d), 1);

  switch (key) {
    case PresetKey.Last7:
      return { from: makeDate(y, m, d - 7), to: endOfYesterday };
    case PresetKey.Last30:
      return { from: makeDate(y, m, d - 30), to: endOfYesterday };
    case PresetKey.Last90:
      return { from: makeDate(y, m, d - 90), to: endOfYesterday };
    case PresetKey.Last12m:
      // The 12 full months before the current one.
      return {
        from: makeDate(y - 1, m, 1),
        to: subtractTimeFromDate(makeDate(y, m, 1), 1),
      };
    case PresetKey.ThisMonth:
      return { from: makeDate(y, m, 1), to: today };
    case PresetKey.PrevMonth:
      return {
        from: makeDate(y, m - 1, 1),
        // need to subtract some milliseconds to obtain end timestamp
        // that doesn't go into next time interval
        to: subtractTimeFromDate(makeDate(y, m, 1), 1),
      };
    case PresetKey.ThisQuarter:
      return { from: makeDate(y, Math.floor(m / 3) * 3, 1), to: today };
    case PresetKey.PrevQuarter: {
      const q = Math.floor(m / 3);
      return {
        from: makeDate(y, (q - 1) * 3, 1),
        to: subtractTimeFromDate(makeDate(y, q * 3, 1), 1),
      };
    }
    case PresetKey.ThisYear:
      return { from: makeDate(y, 0, 1), to: today };
    case PresetKey.PrevYear:
      return {
        from: makeDate(y - 1, 0, 1),
        to: subtractTimeFromDate(makeDate(y, 0, 1), 1),
      };
    case PresetKey.AllTime:
      return { from: makeDate(2019, 0, 1), to: today };
    default:
      return { from: makeDate(y, m, d - 29), to: today };
  }
};

export const toUtcDate = (date: Date, timezone: string): Date =>
  moment
    .tz(
      {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hour: date.getHours(),
        minutes: date.getMinutes(),
        seconds: date.getSeconds(),
      },
      timezone,
    )
    .toDate();

export const toUtcRange = (range: DateRange, timezone: string): DateRange => ({
  from: range.from ? toUtcDate(range.from, timezone) : undefined,
  to: range.to ? toUtcDate(range.to, timezone) : undefined,
});

export const toDisplayDate = (date: Date, timezone: string): Date => {
  const zoned = moment.tz(date, timezone);
  return makeDate(
    zoned.year(),
    zoned.month(),
    zoned.date(),
    zoned.hours(),
    zoned.minutes(),
    zoned.seconds(),
  );
};

export const toDisplayRange = (
  range: DateRange,
  timezone: string,
): DateRange => ({
  from: range.from ? toDisplayDate(range.from, timezone) : undefined,
  to: range.to ? toDisplayDate(range.to, timezone) : undefined,
});
